/**
 * One avatar language for the whole app: a person's initials on a soft
 * pastel tile, the colour picked deterministically from their name so the
 * same person is the same colour on every page, every visit. Pairs come from
 * the design system's ramps — pale background, strong same-hue text.
 *
 * The sidebar account chip stays solid teal on purpose (it is chrome, not
 * content); everywhere a person appears IN content, use this.
 */
const PAIRS: ReadonlyArray<readonly [string, string]> = [
    ["#d6f8fa", "#0a565a"], // teal
    ["#eaf7ee", "#1f8a3d"], // forest
    ["#f0fac2", "#5f8a02"], // lime
    ["#fdf3e2", "#b4790a"], // amber
    ["#fbeaea", "#c23b3b"], // red
    ["#ece7fb", "#5b3e8f"], // violet
    ["#e2f0fb", "#1e5f9e"], // blue
];

export const initials = (name?: string | null): string =>
    (name ?? "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2)
        .map((part) => part[0]!.toUpperCase()).join("");

/** Deterministic [background, text] pair for a name. */
export const avatarPair = (name: string): readonly [string, string] => {
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return PAIRS[hash % PAIRS.length]!;
};

export const InitialsAvatar = ({ name, size = 34, photoUrl }: {
    name?: string | null; size?: number;
    /** When set, the photo replaces the initials tile — same shape, same size. */
    photoUrl?: string | null;
}) => {
    const known = (name ?? "").trim();
    const [bg, fg] = known
        ? avatarPair(known)
        : (["var(--surface-sunk)", "var(--muted)"] as const);
    return (
        <span
            className="person-avatar"
            style={{
                width: size, height: size, flexBasis: size,
                fontSize: Math.max(9, Math.round(size * 0.34)),
                background: bg, color: fg, overflow: "hidden",
            }}
        >
            {photoUrl
                ? <img src={photoUrl} alt={known || "Photo"}
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} />
                : (known ? initials(known) : "?")}
        </span>
    );
};
